import React, { useEffect, useMemo, useRef, useState } from 'react'

import { BoltOutlined, CloseOutlined, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { Box, ButtonBase, Divider, IconButton, Modal, Stack, Typography } from '@mui/material'

import { ChargingPoint, TariffWeekDay } from '@/types/ChargingPoint'
import { numericUtils } from '@/utils/numeric'
import { tariffUtils } from '@/utils/tariff'

interface TariffDetailsModalProps {
  open: boolean
  onClose: () => void
  chargingPoint: ChargingPoint
}

export const TariffDetailsModal: React.FC<TariffDetailsModalProps> = ({ open, onClose, chargingPoint }) => {
  // A linha do tempo vem do backend já resolvida: uma faixa por trecho do dia, sem
  // sobreposição, com o preço que vai ser cobrado. A lista crua de regras (`tariff_rules`)
  // não serve para o cliente — com "preço base + exceções" várias regras cobrem o mesmo
  // horário e ele não teria como saber qual vale.
  const week = useMemo<TariffWeekDay[]>(() => {
    if (chargingPoint.tariff_week_timeline?.length) {
      return chargingPoint.tariff_week_timeline
    }

    return [
      {
        date: '',
        weekday: tariffUtils.currentWeekday(),
        is_today: true,
        segments: chargingPoint.tariff_timeline ?? [],
      },
    ]
  }, [chargingPoint.tariff_week_timeline, chargingPoint.tariff_timeline])

  const [dayIndex, setDayIndex] = useState(0)
  const timelineRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setDayIndex(0)
    }
  }, [open])

  useEffect(() => {
    timelineRef.current?.scrollTo({ top: 0 })
  }, [dayIndex])

  const goToPreviousDay = () => setDayIndex((index) => Math.max(index - 1, 0))
  const goToNextDay = () => setDayIndex((index) => Math.min(index + 1, week.length - 1))

  const day = week[dayIndex] ?? week[0]
  const hasSchedule = tariffUtils.hasDynamicTariffInWeek(week)
  const currentRate = chargingPoint.current_rate_per_kwh ?? chargingPoint.rate_per_kwh

  const currentSegment = useMemo(() => {
    const today = week[0]
    const index = tariffUtils.findCurrentSegmentIndex(today?.segments, tariffUtils.nowInDaySeconds())
    const segment = index >= 0 ? today?.segments[index] : undefined

    if (!segment || Math.abs(Number(segment.rate) - Number(currentRate)) > 0.0001) {
      return null
    }

    return { index, segment }
  }, [week, currentRate])

  const formatPerKwh = (value: string | number | null | undefined): string =>
    `${numericUtils.formatNumberToBRLCurrency(value)}/kWh`

  return (
    <Modal open={open} onClose={onClose} aria-describedby="tariff-details-description">
      <Stack
        direction="column"
        width={360}
        maxWidth="92vw"
        bgcolor="background.paper"
        borderRadius={4}
        padding={3}
        spacing={2}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') goToPreviousDay()
          if (event.key === 'ArrowRight') goToNextDay()
        }}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
          maxHeight: '85vh',
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight="bold">
            Tarifas da estação
          </Typography>
          <IconButton onClick={onClose} edge="end" size="small" aria-label="Fechar">
            <CloseOutlined />
          </IconButton>
        </Stack>
        <Box sx={{ backgroundColor: 'action.hover', borderRadius: 2, paddingX: 2, paddingY: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BoltOutlined color="primary" sx={{ fontSize: 28 }} />
            <Stack minWidth={0}>
              <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
                Agora:
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" lineHeight={1.35}>
                {formatPerKwh(currentRate)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {hasSchedule ? (
          <>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <IconButton size="small" onClick={goToPreviousDay} disabled={dayIndex === 0} aria-label="Dia anterior">
                  <KeyboardArrowLeft />
                </IconButton>

                <Stack alignItems="center" minWidth={0}>
                  <Typography variant="subtitle2" color="text.secondary" textAlign="center" noWrap>
                    {tariffUtils.formatDayLabel(day)}
                    {tariffUtils.formatDayDate(day) && `, ${tariffUtils.formatDayDate(day)}`}
                  </Typography>
                  {day.holiday && (
                    <Typography variant="caption" color="primary" textAlign="center" noWrap>
                      Feriado: {day.holiday}
                    </Typography>
                  )}
                </Stack>

                <IconButton
                  size="small"
                  onClick={goToNextDay}
                  disabled={dayIndex >= week.length - 1}
                  aria-label="Próximo dia"
                >
                  <KeyboardArrowRight />
                </IconButton>
              </Stack>

              {week.length > 1 && (
                <Stack direction="row" spacing={0.5} role="group" aria-label="Dia da semana">
                  {week.map((weekDay, index) => {
                    const isSelected = index === dayIndex

                    return (
                      <ButtonBase
                        key={weekDay.date || index}
                        aria-pressed={isSelected}
                        aria-label={tariffUtils.formatDayLabel(weekDay)}
                        onClick={() => setDayIndex(index)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          paddingY: 0.5,
                          borderRadius: 1,
                          typography: 'caption',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? 'primary.contrastText' : 'text.secondary',
                          bgcolor: isSelected ? 'primary.main' : 'action.hover',
                        }}
                      >
                        {tariffUtils.formatDayShortLabel(weekDay)}
                      </ButtonBase>
                    )
                  })}
                </Stack>
              )}
            </Stack>

            <Box
              ref={timelineRef}
              sx={{ display: 'grid', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', mt: 2 }}
            >
              {week.map((weekDay, index) => (
                <Stack
                  key={weekDay.date || index}
                  spacing={1}
                  aria-hidden={index !== dayIndex}
                  sx={{ gridArea: '1 / 1', visibility: index === dayIndex ? 'visible' : 'hidden', alignSelf: 'start' }}
                >
                  {weekDay.segments.map((segment, segmentIndex) => {
                    const isCurrent = weekDay.is_today && segmentIndex === currentSegment?.index

                    return (
                      <Stack
                        key={`${weekDay.date}-${segment.start}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Stack minWidth={0}>
                          <Typography
                            variant="body2"
                            fontWeight={isCurrent ? 700 : 500}
                            color={isCurrent ? 'primary.main' : undefined}
                          >
                            {tariffUtils.formatTimeRange(segment.start, segment.end)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tariffUtils.formatSegmentLabel(segment)}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                          color={isCurrent ? 'primary.main' : undefined}
                        >
                          {formatPerKwh(segment.rate)}
                        </Typography>
                      </Stack>
                    )
                  })}
                </Stack>
              ))}
            </Box>

            <Divider />

            <Typography id="tariff-details-description" variant="caption" color="text.secondary">
              O valor por kWh muda conforme o dia e o horário da recarga. Sessões que cruzam mais de uma faixa são
              cobradas proporcionalmente ao consumo de cada período.
            </Typography>
          </>
        ) : (
          <Box>
            <Typography id="tariff-details-description" variant="body2" color="text.secondary">
              Esta estação cobra um valor único de {formatPerKwh(currentRate)}, sem variação por horário.
            </Typography>
          </Box>
        )}
      </Stack>
    </Modal>
  )
}
